import React, { useState, useEffect } from 'react';
import { Card, Row, Col, ProgressBar } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { 
  FaEye, FaStar, FaComments, FaHeart, FaShare, FaArrowUp, 
  FaArrowDown, FaMinus, FaChartLine 
} from 'react-icons/fa';

const RealTimeMetrics = ({ userBusinesses }) => {
  const [metrics, setMetrics] = useState({
    totalViews: 0,
    totalReviews: 0,
    totalContacts: 0,
    totalFavorites: 0,
    totalShares: 0,
    avgRating: 0,
    trends: {
      views: 0,
      reviews: 0,
      contacts: 0,
      favorites: 0
    }
  });

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate real-time data updates
    const generateMetrics = () => {
      if (!userBusinesses || userBusinesses.length === 0) {
        return {
          totalViews: 0,
          totalReviews: 0,
          totalContacts: 0,
          totalFavorites: 0,
          totalShares: 0,
          avgRating: 0,
          trends: {
            views: 0,
            reviews: 0,
            contacts: 0,
            favorites: 0
          }
        };
      }

      const totalViews = userBusinesses?.reduce((sum, business) => {
        return sum + (business?.view_count || business?.views || 0);
      }, 0) || 0;
      
      const totalReviews = userBusinesses?.reduce((sum, business) => {
        return sum + (business?.reviews_count || 0);
      }, 0) || 0;
      
      const totalContacts = userBusinesses?.reduce((sum, business) => {
        return sum + (business?.contacts || 0);
      }, 0) || 0;
      
      const totalFavorites = userBusinesses?.reduce((sum, business) => {
        return sum + (business?.favorites || 0);
      }, 0) || 0;
      
      const totalShares = userBusinesses?.reduce((sum, business) => {
        return sum + (business?.shares || 0);
      }, 0) || 0;
      
      let avgRating = 0;
      if (userBusinesses?.length > 0) {
        const totalRating = userBusinesses.reduce((sum, business) => {
          return sum + (business?.rating || 0);
        }, 0);
        avgRating = totalRating / userBusinesses.length;
      }

      // Generate trend data (simulated)
      const trends = {
        views: Math.floor(Math.random() * 20) - 10, // -10 to +10
        reviews: Math.floor(Math.random() * 15) - 7,
        contacts: Math.floor(Math.random() * 25) - 12,
        favorites: Math.floor(Math.random() * 18) - 9
      };

      const roundedAvgRating = Math.round(avgRating * 10) / 10;

      return {
        totalViews,
        totalReviews,
        totalContacts,
        totalFavorites,
        totalShares,
        avgRating: roundedAvgRating,
        trends
      };
    };

    const updateMetrics = () => {
      try {
        setMetrics(generateMetrics());
        setIsLoading(false);
      } catch (error) {
        console.error('Error updating metrics:', error);
        setIsLoading(false);
      }
    };

    // Initial load
    updateMetrics();

    // Update every 30 seconds
    const interval = setInterval(updateMetrics, 30000);

    // Cleanup function
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [userBusinesses]);

  const getTrendIcon = (trend) => {
    if (trend > 0) return <FaArrowUp className="text-success" />;
    if (trend < 0) return <FaArrowDown className="text-danger" />;
    return <FaMinus className="text-muted" />;
  };

  const getTrendColor = (trend) => {
    if (trend > 0) return 'success';
    if (trend < 0) return 'danger';
    return 'secondary';
  };

  const metricCards = [
    {
      title: 'Total Views',
      value: metrics.totalViews.toLocaleString(),
      icon: <FaEye />,
      color: '#667eea',
      trend: metrics.trends.views,
      progress: Math.min((metrics.totalViews / 10000) * 100, 100)
    },
    {
      title: 'Total Reviews',
      value: metrics.totalReviews.toLocaleString(),
      icon: <FaStar />,
      color: '#ed8936',
      trend: metrics.trends.reviews,
      progress: Math.min((metrics.totalReviews / 100) * 100, 100)
    },
    {
      title: 'Contacts',
      value: metrics.totalContacts.toLocaleString(),
      icon: <FaComments />,
      color: '#48bb78',
      trend: metrics.trends.contacts,
      progress: Math.min((metrics.totalContacts / 200) * 100, 100)
    },
    {
      title: 'Favorites',
      value: metrics.totalFavorites.toLocaleString(),
      icon: <FaHeart />,
      color: '#e53e3e',
      trend: metrics.trends.favorites,
      progress: Math.min((metrics.totalFavorites / 50) * 100, 100)
    },
    {
      title: 'Shares',
      value: metrics.totalShares.toLocaleString(),
      icon: <FaShare />,
      color: '#9f7aea',
      trend: Math.floor(Math.random() * 20) - 10,
      progress: Math.min((metrics.totalShares / 100) * 100, 100)
    },
    {
      title: 'Avg Rating',
      value: metrics.avgRating.toFixed(1),
      icon: <FaChartLine />,
      color: '#38b2ac',
      trend: Math.floor(Math.random() * 6) - 3,
      progress: (metrics.avgRating / 5) * 100
    }
  ];

  if (isLoading) {
    return (
      <Row className="mb-4">
        <Col md={12} className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading metrics...</span>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="mb-4">
      {metricCards.map((metric, index) => (
        <Col key={metric.title} md={6} lg={4} className="mb-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
          >
            <Card className="metric-card h-100">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div className="metric-icon" style={{ color: metric.color }}>
                    {metric.icon}
                  </div>
                  <div className="metric-trend">
                    {getTrendIcon(metric.trend)}
                    <span className={`ms-1 text-${getTrendColor(metric.trend)}`}>
                      {Math.abs(metric.trend)}%
                    </span>
                  </div>
                </div>
                
                <div className="metric-content">
                  <h3 className="metric-value">{metric.value}</h3>
                  <p className="metric-title">{metric.title}</p>
                  
                  <div className="metric-progress">
                    <ProgressBar 
                      now={metric.progress} 
                      variant={getTrendColor(metric.trend)}
                      style={{ height: '6px', borderRadius: '3px' }}
                    />
                    <small className="text-muted mt-1 d-block">
                      {metric.progress.toFixed(1)}% of target
                    </small>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </motion.div>
        </Col>
      ))}
    </Row>
  );
};

export default RealTimeMetrics;
